import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import { RootState } from '../../reducers';
import Dropdown, { DropdownOption } from '../Dropdown';
import UpdateUserModal from '../updateUserModal';
import SsoConfigModal, { SSO_MODAL_MODE } from './SsoConfigModal';
import { isRoleTitan } from '../utils';
import { fetchPartners } from '../../actions/adminActions';
import {
  fetchSsoConfigs,
  saveSsoConfig,
  deleteSsoConfig,
  closeSsoConfigModal,
} from '../../actions/ssoConfigActions';
import {
  PartnerDetailsInfo,
  PartnerInfo,
} from '../../reducers/userManagementReducer';
import {
  SsoConfigListInfo,
  SaveSsoConfigInfo,
  DeleteSsoConfigInfo,
} from '../../reducers/ssoConfigReducer';
import {
  SsoConfigDetail,
  SsoConfigPayload,
} from '../../model/ssoConfigPayload';

type Props = {
  token: string;
  userRole: string;
  userPartners: PartnerInfo[];
  partnerDetails: PartnerDetailsInfo;
  configListDetails: SsoConfigListInfo;
  saveConfigDetails: SaveSsoConfigInfo;
  deleteConfigDetails: DeleteSsoConfigInfo;
  fetchPartners: (token: string) => void;
  fetchSsoConfigs: (partnerId: number, token: string) => void;
  saveSsoConfig: (
    payload: SsoConfigPayload,
    isEdit: boolean,
    token: string
  ) => void;
  deleteSsoConfig: (
    payload: { domain: string; partnerId: number },
    token: string
  ) => void;
  closeSsoConfigModal: () => void;
} & WrappedComponentProps;

interface State {
  selectedPartnerId: number | null;
  shouldShowModal: boolean;
  modalMode: SSO_MODAL_MODE | 'delete' | null;
  selectedConfig: SsoConfigDetail | null;
}

class SsoConfig extends React.Component<Props> {
  state: State = {
    selectedPartnerId: null,
    shouldShowModal: false,
    modalMode: null,
    selectedConfig: null,
  };

  componentDidMount() {
    if (
      isRoleTitan(this.props.userRole) &&
      this.props.partnerDetails.partnerList.length <= 0
    ) {
      this.props.fetchPartners(this.props.token);
    }
    this.autoSelectPartner();
  }

  componentDidUpdate() {
    this.autoSelectPartner();
  }

  getPartnerList = (): PartnerInfo[] => {
    if (isRoleTitan(this.props.userRole)) {
      return this.props.partnerDetails.partnerList;
    }
    return this.props.userPartners || [];
  };

  autoSelectPartner = () => {
    if (this.state.selectedPartnerId !== null) {
      return;
    }
    const partnerList = this.getPartnerList();
    if (isRoleTitan(this.props.userRole)) {
      // titan admin picks a partner; auto-select only when there is exactly one
      if (partnerList.length === 1) {
        this.selectPartner(partnerList[0].partnerId);
      }
    } else if (partnerList.length > 0) {
      // partner admin is locked to their own partner
      this.selectPartner(partnerList[0].partnerId);
    }
  };

  selectPartner = (partnerId: number) => {
    this.setState({ selectedPartnerId: partnerId });
    this.props.fetchSsoConfigs(partnerId, this.props.token);
  };

  handlePartnerClick = (item: DropdownOption) => {
    if (item.value !== this.state.selectedPartnerId) {
      this.selectPartner(item.value);
    }
  };

  handleAddClick = () => {
    this.setState({
      shouldShowModal: true,
      modalMode: SSO_MODAL_MODE.CREATE,
      selectedConfig: null,
    });
  };

  handleEditClick = (config: SsoConfigDetail) => {
    this.setState({
      shouldShowModal: true,
      modalMode: SSO_MODAL_MODE.EDIT,
      selectedConfig: config,
    });
  };

  handleDeleteClick = (config: SsoConfigDetail) => {
    this.setState({
      shouldShowModal: true,
      modalMode: 'delete',
      selectedConfig: config,
    });
  };

  closeModal = (shouldRefetch: boolean) => {
    this.setState({
      shouldShowModal: false,
      modalMode: null,
      selectedConfig: null,
    });
    this.props.closeSsoConfigModal();
    if (shouldRefetch && this.state.selectedPartnerId !== null) {
      this.props.fetchSsoConfigs(this.state.selectedPartnerId, this.props.token);
    }
  };

  deleteSelectedConfig = () => {
    if (this.state.selectedConfig === null) return;
    this.props.deleteSsoConfig(
      {
        domain: this.state.selectedConfig.domain,
        partnerId: this.state.selectedConfig.partnerId,
      },
      this.props.token
    );
  };

  getModalContent = () => {
    switch (this.state.modalMode) {
      case SSO_MODAL_MODE.CREATE:
      case SSO_MODAL_MODE.EDIT:
        return (
          <SsoConfigModal
            mode={this.state.modalMode}
            config={this.state.selectedConfig}
            partnerId={this.state.selectedPartnerId as number}
            saveConfigDetails={this.props.saveConfigDetails}
            saveSsoConfig={(payload: SsoConfigPayload, isEdit: boolean) =>
              this.props.saveSsoConfig(payload, isEdit, this.props.token)
            }
            closeModal={this.closeModal}
          />
        );
      case 'delete':
        return (
          <UpdateUserModal
            heading={this.props.intl.formatMessage({
              id: 'sso-config-delete-heading',
            })}
            subheading={this.state.selectedConfig?.domain || ''}
            description={this.props.intl.formatMessage({
              id: 'sso-config-delete-message',
            })}
            isLoadingInProgress={
              this.props.deleteConfigDetails.isDeleteConfigRequestInProgress
            }
            shouldShowError={
              this.props.deleteConfigDetails.deleteConfigRequestError.length > 0
            }
            shouldShowSuccess={
              this.props.deleteConfigDetails.isDeleteConfigRequestSuccess
            }
            buttonAction={this.deleteSelectedConfig}
            errorText={this.props.deleteConfigDetails.deleteConfigRequestError}
            closeAction={this.closeModal}
          />
        );
      default:
        return null;
    }
  };

  render() {
    const partnerList = this.getPartnerList();
    const partnerDropdownList: DropdownOption[] = partnerList.map(
      (partnerInfo) => ({
        label: partnerInfo.partnerName,
        value: partnerInfo.partnerId,
        isSelected: partnerInfo.partnerId === this.state.selectedPartnerId,
      })
    );
    const selectedPartnerIndex = partnerList.findIndex(
      (partnerInfo) => partnerInfo.partnerId === this.state.selectedPartnerId
    );
    const selectedPartner = partnerList.find(
      (partnerInfo) => partnerInfo.partnerId === this.state.selectedPartnerId
    );
    const isTitanAdmin = isRoleTitan(this.props.userRole);
    const {
      isConfigListFetchInProgress,
      isConfigListFetchSuccess,
      configListFetchError,
      configList,
    } = this.props.configListDetails;

    const configRows = configList.map((config: SsoConfigDetail) => (
      <tr key={config.domain}>
        <td className="sso-config-table-cell">{config.domain}</td>
        <td className="sso-config-table-cell">{config.provider}</td>
        <td className="sso-config-table-cell sso-config-url-cell">
          {config.issuerUrl}
        </td>
        <td className="sso-config-table-cell">{config.clientId}</td>
        <td className="sso-config-table-cell">{config.authMethod}</td>
        <td className="sso-config-table-actions">
          <span
            className="sso-config-action-link"
            onClick={() => this.handleEditClick(config)}
          >
            <FormattedMessage id="sso-config-edit" />
          </span>
          <span
            className="sso-config-action-link sso-config-action-delete"
            onClick={() => this.handleDeleteClick(config)}
          >
            <FormattedMessage id="sso-config-delete" />
          </span>
        </td>
      </tr>
    ));

    return (
      <div className="user-list-container">
        <div className="user-list-manage-accounts">
          <div>
            <FormattedMessage id="sso-config-heading" />
          </div>
          <div>
            <button
              className="button button-primary add-user-button"
              disabled={this.state.selectedPartnerId === null}
              onClick={this.handleAddClick}
            >
              <FormattedMessage id="add-sso-config" />
            </button>
          </div>
        </div>
        <div className="sso-config-partner-selector">
          <div className="input-box-heading">
            <FormattedMessage id="partner" />
          </div>
          {isTitanAdmin ? (
            this.props.partnerDetails.isPartnersInfoFetchInProgress ? (
              <div className="log-loader" />
            ) : (
              <Dropdown
                key={partnerDropdownList.length}
                mainClass="dropdown-container"
                headerClass="dropdown-header"
                headerTitleClass="dropdown-header-title"
                listContainerClass="dropdown-list-container"
                dropDownItemClass="role-dropdown-menu-item"
                onItemClick={this.handlePartnerClick}
                defaultItemSelectedIndexes={
                  selectedPartnerIndex !== -1 ? [selectedPartnerIndex] : null
                }
                placeholderText={this.props.intl.formatMessage({
                  id: 'select_partner_placeholder',
                })}
                placeholderClass="partner-dropdown-placeholder"
                shouldAllowMultiSelect={false}
                list={partnerDropdownList}
              />
            )
          ) : (
            <div className="dropdown-container sso-config-partner-disabled">
              <div className="dropdown-header-title">
                {selectedPartner ? selectedPartner.partnerName : ''}
              </div>
            </div>
          )}
        </div>
        {isConfigListFetchInProgress ? (
          <div className="log-loader" />
        ) : configListFetchError ? (
          <div className="create-user-error">
            <FormattedMessage id="sso-config-fetch-error" />
          </div>
        ) : isConfigListFetchSuccess && configList.length === 0 ? (
          <div className="sso-config-empty-state">
            <FormattedMessage id="sso-config-no-configs" />
          </div>
        ) : configList.length > 0 ? (
          <div className="user-list-table-container">
            <table className="user-list-table">
              <tbody>
                <tr>
                  <th className="sso-config-table-cell">
                    <FormattedMessage id="sso-config-domain" />
                  </th>
                  <th className="sso-config-table-cell">
                    <FormattedMessage id="sso-config-provider" />
                  </th>
                  <th className="sso-config-table-cell">
                    <FormattedMessage id="sso-config-issuer-url" />
                  </th>
                  <th className="sso-config-table-cell">
                    <FormattedMessage id="sso-config-client-id" />
                  </th>
                  <th className="sso-config-table-cell">
                    <FormattedMessage id="sso-config-auth-method" />
                  </th>
                  <th />
                </tr>
                {configRows}
              </tbody>
            </table>
          </div>
        ) : null}
        {this.state.shouldShowModal ? (
          <div className="modal">
            <div className="modal-content sso-config-modal">
              {this.getModalContent()}
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    token: state.auth.token,
    userRole: state.auth.userRole,
    userPartners: state.auth.userPartners,
    partnerDetails: state.users.partnerDetails,
    configListDetails: state.ssoConfig.configListDetails,
    saveConfigDetails: state.ssoConfig.saveConfigDetails,
    deleteConfigDetails: state.ssoConfig.deleteConfigDetails,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    ...bindActionCreators(
      {
        fetchPartners,
        fetchSsoConfigs,
        saveSsoConfig,
        deleteSsoConfig,
        closeSsoConfigModal,
      },
      dispatch
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(SsoConfig));
